"use client";

import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";

export type CcPillTabItem = {
  id: string;
  label: ReactNode;
  /** Overrides the tab button's `id` (defaults to `id`). */
  tabId?: string;
  /** Per-tab `aria-controls`; falls back to `tabPanelId` when omitted. */
  controlsId?: string;
};

type CcPillTabListProps = {
  items: CcPillTabItem[];
  activeId: string;
  onSelect: (id: string) => void;
  ariaLabel: string;
  scrollClassName?: string;
  tabClassName?: string;
  /** When set, forwarded to each tab's `aria-controls`. */
  tabPanelId?: string;
};

export function CcPillTabList({
  items,
  activeId,
  onSelect,
  ariaLabel,
  scrollClassName,
  tabClassName,
  tabPanelId,
}: CcPillTabListProps) {
  const tabRefs = useRef(new Map<string, HTMLButtonElement>());
  const [indicator, setIndicator] = useState({ left: 0, width: 0, ready: false });

  const syncIndicator = useCallback(() => {
    const activeButton = tabRefs.current.get(activeId);
    if (!activeButton) {
      return;
    }
    setIndicator({
      left: activeButton.offsetLeft,
      width: activeButton.offsetWidth,
      ready: true,
    });
  }, [activeId]);

  useLayoutEffect(() => {
    syncIndicator();
    window.addEventListener("resize", syncIndicator);
    return () => window.removeEventListener("resize", syncIndicator);
  }, [syncIndicator, items]);

  const scrollClasses = ["cc-pill-tabs__scroll", scrollClassName].filter(Boolean).join(" ");
  const tabClasses = ["cc-pill-tabs__tab", tabClassName].filter(Boolean).join(" ");

  return (
    <div className={scrollClasses}>
      <div className="cc-pill-tabs" role="tablist" aria-label={ariaLabel}>
        <span
          className="cc-pill-tabs__indicator"
          aria-hidden="true"
          style={
            {
              "--cc-pill-tab-left": `${indicator.left}px`,
              "--cc-pill-tab-width": `${indicator.width}px`,
              opacity: indicator.ready ? 1 : 0,
            } as CSSProperties
          }
        />
        {items.map((item) => (
          <button
            key={item.id || "all"}
            ref={(node) => {
              if (node) tabRefs.current.set(item.id, node);
              else tabRefs.current.delete(item.id);
            }}
            type="button"
            role="tab"
            id={item.tabId ?? item.id}
            aria-selected={activeId === item.id}
            aria-controls={item.controlsId ?? tabPanelId}
            className={tabClasses}
            onClick={() => onSelect(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>
    </div>
  );
}
